/**
 * DsoTrendTile — kompakter DSO-Ampel-/Trend-Begleiter der „Handlungs-Fokus"-
 * Zone (Design-Identität v5 „Kontor", `architecture-ux-design-v5.md` §6.4,
 * Arbeitspaket M-D5-C1).
 *
 * Sitzt neben `OverdueFocusList` (Zone B2, halbbreit) und verdichtet die
 * Forderungs-Gesundheit auf einen Blick:
 *  - Ampel-Punkt (Laub/Amber/Rot — Status-Töne §5, NICHT Marken-Mint) +
 *    Tageszahl im Data-Face + Trend-Pfeil (Icon-Wrapper `IconTrend`/
 *    `IconTrendDown`/`IconTrendFlat`).
 *  - Subzeile „Überfällig-Quote" = `overdueCents / openCents` als 4px-Mini-
 *    Balken (Füllgrad + Status-Ton; KEIN Chart-Framework).
 *
 * Datengrundlage ist AUSSCHLIESSLICH das bereits auf CockpitPage-Ebene geladene
 * {@link CockpitOpenItemRow}[] (0 zusätzliche Firestore-Reads) über die
 * bestehenden puren Funktionen {@link computeDso} + {@link summarizeOpenItems}.
 *
 * Fail-closed (§6.4): solange die Zeilen nicht geladen sind → Lade-Spinner;
 * bei Ladefehler oder {@link computeDso}`.kind !== 'ok'` → neutraler Hinweis
 * „Noch zu wenige bezahlte Rechnungen" (spiegelt die DsoTile-Degradation).
 * A11y (WCAG 1.4.1): Status wird nie NUR über Farbe transportiert — die Ampel
 * trägt zusätzlich das ausgeschriebene Textlabel, der Trend ein `aria-label`.
 *
 * @module components/cockpit/DsoTrendTile
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Spinner } from '../common/Spinner';
import { IconTrend, IconTrendDown, IconTrendFlat, type FbIcon } from '../../icons';
import { computeDso, type DsoResult } from '../../utils/cockpitReceivables';
import { summarizeOpenItems, type CockpitOpenItemRow } from '../../loaders/cockpitLoaders';

/**
 * DSO-Ampel-Zone → CSS-Status-Ton-Var (§5). Laub (Erfolg-Grün, NICHT Mint) für
 * „grün", Amber für „gelb", Rot für „rot". Wird sowohl für den Ampel-Punkt als
 * auch für den Füllstand des Überfällig-Quoten-Balkens verwendet.
 */
const LIGHT_TONE_VAR: Record<'green' | 'yellow' | 'red', string> = {
  green: 'var(--tone-success-fg)',
  yellow: 'var(--tone-warning-fg)',
  red: 'var(--tone-danger-fg)',
};

export interface DsoTrendTileProps {
  /** Gebubbelte offene-Posten-Zeilen; `null` ⇒ noch am Laden. */
  rows: CockpitOpenItemRow[] | null;
  /** Gebubbelter Ladefehler der offenen Posten. */
  rowsError: boolean;
  /** Referenzdatum `YYYY-MM-DD` (Parameter für deterministisches Rendern/Tests). */
  todayISO: string;
}

/**
 * @param props - {@link DsoTrendTileProps}.
 */
export const DsoTrendTile: React.FC<DsoTrendTileProps> = ({ rows, rowsError, todayISO }) => {
  const { t } = useTranslation('cockpit');

  const header = (
    <div className="flex items-center gap-2 mb-2 text-secondary">
      <h3 className="text-sm font-semibold">{t('cfo.dsoTrend.title')}</h3>
    </div>
  );

  // Ladezustand (Zeilen noch nicht gebubbelt, kein Fehler): Spinner statt „—".
  if (rows === null && !rowsError) {
    return (
      <Card>
        {header}
        <div className="flex justify-center py-2">
          <Spinner size="sm" />
        </div>
      </Card>
    );
  }

  const dso: DsoResult =
    rows && !rowsError ? computeDso(rows, todayISO) : { kind: 'insufficient', sampleCount: 0 };

  // Fail-closed: keine belastbare Kennzahl ⇒ neutraler Hinweis (DsoTile-Degradation).
  if (dso.kind !== 'ok') {
    return (
      <Card>
        {header}
        <p className="text-sm text-secondary">{t('cfo.dsoTrend.insufficient')}</p>
      </Card>
    );
  }

  const summary = summarizeOpenItems(rows ?? []);
  const fraction =
    summary.openCents > 0 ? Math.min(1, Math.max(0, summary.overdueCents / summary.openCents)) : 0;
  const pct = Math.round(fraction * 100);
  const toneVar = LIGHT_TONE_VAR[dso.light];
  const lightLabel = t(`cfo.dso.light.${dso.light}`);

  return (
    <Card>
      {header}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid="dso-ampel-dot"
          data-tone={dso.light}
          className="inline-block w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: toneVar }}
          aria-hidden="true"
        />
        <span className="font-data tabular-nums text-2xl font-bold text-primary">
          {t('cfo.dso.days', { count: dso.dsoDays })}
        </span>
        <span className="text-xs font-medium" style={{ color: toneVar }}>
          {lightLabel}
        </span>
        {dso.trend && (
          <DsoTrendArrow direction={dso.trend.direction} prevDsoDays={dso.trend.prevDsoDays} t={t} />
        )}
      </div>

      {/* Überfällig-Quote als 4px-Mini-Balken (kein Chart-Framework). */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-secondary mb-1">
          <span>{t('cfo.dsoTrend.overdueQuote')}</span>
          <span className="font-data tabular-nums">{t('cfo.dsoTrend.overdueQuoteValue', { pct })}</span>
        </div>
        <div
          className="h-1 w-full rounded-full overflow-hidden bg-[var(--surface-inset)]"
          role="img"
          aria-label={t('cfo.dsoTrend.overdueQuoteAria', { pct })}
        >
          <div className="h-1 rounded-full" style={{ width: `${pct}%`, backgroundColor: toneVar }} />
        </div>
      </div>
    </Card>
  );
};

/**
 * Trend-Pfeil mit ausgeschriebenem `aria-label` (Status nie nur über Icon/Farbe).
 * `up` = DSO gestiegen (schlechter), `down` = gesunken (besser), `flat` =
 * unverändert.
 */
const DsoTrendArrow: React.FC<{
  direction: 'up' | 'down' | 'flat';
  prevDsoDays: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}> = ({ direction, prevDsoDays, t }) => {
  const Icon: FbIcon =
    direction === 'up' ? IconTrend : direction === 'down' ? IconTrendDown : IconTrendFlat;
  const directionWord = t(`cfo.dso.trend.${direction}`);
  const label = t('cfo.dso.trendAria', { direction: directionWord, days: prevDsoDays });
  return (
    <span className="inline-flex items-center text-secondary" role="img" aria-label={label}>
      <Icon aria-hidden={true} />
    </span>
  );
};

export default DsoTrendTile;
